import type { NormalizedCacheObject } from 'apollo-cache-inmemory';
import type { ApolloClientConfig } from './types';

import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { from } from 'apollo-link';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { getCurrentInstance } from 'vue';

import possibleTypes from '@/@types/graphql-fragment-types.json';

// Install the vue plugin
Vue.use(VueApollo);

async function createApolloClient({
	headers,
	pinia,
}: ApolloClientConfig): Promise<ApolloClient<NormalizedCacheObject>> {
	const fragmentMatcher = new IntrospectionFragmentMatcher({
		introspectionQueryResultData: possibleTypes,
	});

	// Cache implementation
	const cache = new InMemoryCache({ fragmentMatcher });

	// If on the client, recover the injected state
	if (process.client && window?.__APOLLO_STATE__ != null) {
		cache.restore(window.__APOLLO_STATE__.defaultClient);
	}

	// Create the apollo client
	const ssrConfig = process.server ? { ssrMode: true } : { ssrForceFetchDelay: 500 };
	const connectToDevTools = process.env.NODE_ENV !== 'production';

	let links = [];

	if (process.server) {
		const { getServerLinks } = await import('@/graphql/utils');
		links = await getServerLinks({ headers, pinia });
	} else {
		const { getClientLinks } = await import('@/graphql/utils');
		links = await getClientLinks({ headers, pinia });
	}

	return new ApolloClient({
		link: from(links),
		cache,
		connectToDevTools,
		...ssrConfig,
	});
}

export async function getApolloProvider({ headers, pinia }: ApolloClientConfig) {
	return new VueApollo({
		// fresh client created per request to prevent shared state
		defaultClient: await createApolloClient({ headers, pinia }),
	});
}

/**
 * Reads the client from the current Vue instance's $apollo provider,
 * so it always gets the request-scoped client, never a stale singleton.
 */
export function useApolloClient(): ApolloClient<NormalizedCacheObject> {
	const vm = getCurrentInstance();
	const client = vm?.proxy?.$apollo.provider?.defaultClient;

	if (!client) {
		throw new Error(
			'Apollo client not found. Ensure useApolloClient() is called inside a component setup() with VueApollo installed.'
		);
	}

	return client;
}
